import React, { useMemo } from "react";
import "./css/ScamActivityMap.css";

// Emoji icons per scam type — meaningful even without colour
const SCAM_TYPE_ICONS = {
  "Romance Scam": "💔",
  Phishing: "🎣",
  "Tech Support Scam": "💻",
  "Investment Scam": "💰",
  "Employment Scam": "💼",
  "Online Shopping Scam": "🛒",
  "Government Impersonation": "🏛️",
  "Lottery Scam": "🎰",
  "Charity Scam": "🙏",
  "Phone Scam": "📞",
  Other: "⚠️",
};

const SOURCE_LABELS = { BBB: "BBB Scam Tracker", FTC: "FTC Consumer Sentinel" };

/**
 * A single scam incident card used in both the list view and map info-windows.
 *
 * @param {Object}  props.incident  - ScamIncidentDTO
 * @param {boolean} props.expanded  - Whether the full detail is shown (list view)
 * @param {Function} props.onToggle - Toggle expand/collapse in list view
 * @param {boolean} props.compact   - Compact mode for map InfoWindow
 */
function ScamActivityIncidentCard({ incident, expanded, onToggle, compact }) {
  const formattedDate = useMemo(() => {
    if (!incident?.reportedAt) return "";
    try {
      const d = new Date(incident.reportedAt + "T00:00:00");
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return incident.reportedAt;
    }
  }, [incident?.reportedAt]);

  if (!incident) return null;

  const icon = SCAM_TYPE_ICONS[incident.scamType] || "⚠️";
  const sourceLabel = SOURCE_LABELS[incident.source] || incident.source;

  if (compact) {
    // Compact mode: shown inside a Google Maps InfoWindow — keep it very brief
    return (
      <div className="sam-card sam-card--compact">
        <div className="sam-card__header">
          <span className="sam-card__icon">{icon}</span>
          <div>
            <span className="sam-card__type">{incident.scamType}</span>
            <span className="sam-card__location">
              {incident.city}, {incident.state}
            </span>
          </div>
        </div>
        <div className="sam-card__meta-row">
          <span className="sam-card__date">{formattedDate}</span>
          <span className="sam-card__distance">
            {incident.distanceMiles} mi away
          </span>
        </div>
        {incident.description && (
          <p className="sam-card__desc sam-card__desc--truncated">
            {incident.description.length > 120
              ? incident.description.substring(0, 120) + "…"
              : incident.description}
          </p>
        )}
        {incident.sourceUrl && (
          <a
            href={incident.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="sam-card__source-link"
          >
            View on {sourceLabel} ↗
          </a>
        )}
      </div>
    );
  }

  // Full card for list view
  return (
    <div
      className={`sam-card${expanded ? " sam-card--expanded" : ""}`}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onToggle && onToggle()}
      aria-expanded={expanded}
    >
      {/* ── Header row ── */}
      <div className="sam-card__header">
        <span className="sam-card__icon" aria-hidden="true">
          {icon}
        </span>
        <div className="sam-card__header-text">
          <span className="sam-card__type">{incident.scamType}</span>
          <span className="sam-card__location">
            {[incident.city, incident.state].filter(Boolean).join(", ")}
            {incident.zipCode ? ` ${incident.zipCode}` : ""}
          </span>
        </div>
        <div className="sam-card__header-meta">
          <span className="sam-card__distance">
            📍 {incident.distanceMiles} mi
          </span>
          <span className="sam-card__date">{formattedDate}</span>
        </div>
        <span className="sam-card__chevron" aria-hidden="true">
          {expanded ? "▲" : "▼"}
        </span>
      </div>

      {/* Source badge */}
      <div className="sam-card__source-badge">{sourceLabel}</div>

      {/* ── Description (always shown, full when expanded) ── */}
      {incident.description && (
        <p
          className={`sam-card__desc${!expanded ? " sam-card__desc--truncated" : ""}`}
        >
          {incident.description}
        </p>
      )}

      {/* ── Expanded content ── */}
      {expanded && (
        <div className="sam-card__expanded-body">
          {incident.redFlags && incident.redFlags.length > 0 && (
            <div className="sam-card__red-flags">
              <h4 className="sam-card__section-title">⚠️ Red Flags</h4>
              <ul className="sam-card__flag-list">
                {incident.redFlags.map((flag, i) => (
                  <li key={i}>{flag}</li>
                ))}
              </ul>
            </div>
          )}
          {incident.sourceUrl && (
            <a
              href={incident.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="sam-card__source-link"
              onClick={(e) => e.stopPropagation()}
            >
              View original report on {sourceLabel} ↗
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default ScamActivityIncidentCard;
